import React, { FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import FlagIcon from '@mui/icons-material/Flag';
import { SearchResult } from 'models/search-result';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { SampleType } from 'models/order';

type ResultCardProps = {
    searchResult: SearchResult;
    userType: string;
};
const ResultCard: FC<ResultCardProps> = ({ searchResult, userType }: ResultCardProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    const displayFlagIcon = permissions.isLabTechnician(userType) && searchResult.labFlag;
    const isInternalOrderId = permissions.isLabTechnician(userType) && searchResult.internalOrderId;
    const sampleType = searchResult.deliveryDetails.sample?.type;

    const translatedSimpleConstructionName = (simpleConstructionName: string): string => {
        const names = simpleConstructionName.split(',');
        return names.map((name) => translate(name)).join(', ');
    };

    return (
        <Box
            sx={{
                minWidth: 350,
                position: 'relative',
                paddingTop: 0.5,
                paddingBottom: 0.5,
            }}
        >
            <Typography variant="subtitle2" className="mouseflow-mask">
                {searchResult.patient.firstName + ' ' + searchResult.patient.lastName}
            </Typography>
            <Box className="mouseflow-mask">{searchResult.patient.personalIdentityNumber}</Box>
            <Box style={{ wordWrap: 'break-word' }}>
                {searchResult.orderId}&nbsp;-&nbsp;
                {searchResult.actionSubCategoryName && searchResult.actionSubCategoryName !== '' ? (
                    <span>{translate('orders:' + searchResult.actionSubCategoryName)}</span>
                ) : (
                    <span>{translatedSimpleConstructionName(searchResult.simpleConstructionName)}</span>
                )}
            </Box>
            {isInternalOrderId ? <Box>{searchResult.internalOrderId}</Box> : undefined}
            <Typography variant="caption">
                {translate('common:CREATED')}: {searchResult.createdDateAndTime}
            </Typography>
            <Box
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: 1,
                }}
            >
                {displayFlagIcon ? (
                    <FlagIcon
                        sx={{
                            height: 25,
                            color: '#ff0000',
                            marginRight: 1,
                        }}
                    />
                ) : undefined}
                <Box height="20px" mr={1}>
                    {sampleType === SampleType.DIGITAL ? (
                        <Tooltip title={<span>{translate('orders:DIGITAL')}</span>}>
                            <AttachFileIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                    {sampleType === SampleType.PHYSICAL ? (
                        <Tooltip title={<span>{translate('orders:PHYSICAL')}</span>}>
                            <Inventory2OutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                    {sampleType === SampleType.NO_SAMPLE ? (
                        <Tooltip title={<span>{translate('orders:NO_SAMPLE')}</span>}>
                            <CircleOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                </Box>
            </Box>
        </Box>
    );
};

export default ResultCard;
